import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { xcss, Box, Text, Stack, Inline } from '@atlaskit/primitives';
import Link from '@atlaskit/link';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { LinkButton } from '@atlaskit/button/new';
import Image from '@atlaskit/image';

import { LivePagesBetaLozenge } from '@confluence/live-pages-features/entry-points/LivePagesLozenges';
import { LivePagesInProductImage } from '@confluence/live-pages-features/entry-points/LivePagesInProductImage';
import { ADMIN_NEW_FEATURES } from '@confluence/named-routes';

const LIVE_PAGES_OPEN_BETA_USER_DOC_URL =
	'https://support.atlassian.com/confluence-cloud/docs/create-and-collaborate-in-real-time-with-live-docs/';

const bannerStyles = xcss({
	border: `${token('border.width')} solid ${token('color.background.discovery.hovered')}`,
	borderRadius: 'border.radius.200',
	background: `linear-gradient(90deg, ${token(
		'color.background.accent.teal.subtlest',
	)}, ${token('color.background.discovery')})`,
});

const contentStyles = xcss({
	marginBlock: 'space.300',
});

const i18n = defineMessages({
	bannerHeading: {
		id: 'admin-center.live-pages-admin-info-banner.beta.heading',
		defaultMessage: 'Live docs are now available',
		description:
			'Title of the banner informing the site admin that the live docs are now available',
	},
	bannerBody: {
		id: 'admin-center.live-pages-admin-info-banner.beta.body',
		defaultMessage:
			'Create and edit without having to publish. <link>Find out more about live docs</link>',
		description:
			'Link to find out more about live pages. Clicking this button takes the user to documentation about live docs.',
	},
	enableLivePages: {
		id: 'admin-center.live-pages-admin-info-banner.beta.enable-live-pages',
		defaultMessage: 'Try live docs now',
		description: 'Button to that will turn on the live docs feature',
	},
});

const BannerTitle = () => (
	<Inline alignInline="center" space="space.100">
		<Heading size="small">
			<FormattedMessage {...i18n.bannerHeading} />
		</Heading>
		<Box>
			<LivePagesBetaLozenge bold />
		</Box>
	</Inline>
);
const BannerBody = () => (
	<Text>
		<FormattedMessage
			{...i18n.bannerBody}
			values={{
				link: (chunks: React.ReactNode[]) => (
					<Link href={LIVE_PAGES_OPEN_BETA_USER_DOC_URL} target="_blank">
						{chunks}
					</Link>
				),
			}}
		/>
	</Text>
);
const BannerFooter = () => (
	<LinkButton appearance="primary" href={ADMIN_NEW_FEATURES.toUrl()}>
		<FormattedMessage {...i18n.enableLivePages} />
	</LinkButton>
);

const BannerContent = () => (
	<Stack space="space.100" alignInline="start" xcss={contentStyles}>
		<BannerTitle />
		<BannerBody />
		<BannerFooter />
	</Stack>
);

export const LivePagesBetaAdminBanner = () => (
	<Box xcss={bannerStyles} paddingInline="space.300">
		<Inline alignInline="center" alignBlock="center" space="space.100" spread="space-between">
			<BannerContent />
			<Image src={LivePagesInProductImage} alt="" />
		</Inline>
	</Box>
);
